import {isEmpty} from 'lodash'
import {SubmitHandler} from 'react-hook-form'

import {LoadType} from '../../../../App.constants'
import {trackEvent} from '../../../../common/analytics'
import {BulkOrderFormValues} from '../../../BulkOrdersFormValues'
import {DEFAULT_CUSTOMER_REFERENCE} from '../../../declarations/constants'
import {ShippingType} from '../../../declarations/OrderIntake.enums'
import {
  OrderRequest,
  OrderRequestGroupErrorResponse,
  OrderRequestGroupResponse,
  OrderRequestGroupSuccessResponse
} from '../../../declarations/types'
import {
  buildSplitLoadPayload,
  mapSplitLoadPayloadToV2,
  SplitLoadOrderIntakePayload,
  V2SplitLoadOrderIntakePayload
} from '../../containers/SplitLoad/buildSplitLoadPayload'

interface UseSplitLoadSubmitParams {
  customerId: string | undefined
  defaultDeliveryBusinessLineCode: string
  hasSiteContactNameAndPhone: boolean
  defaultQuantityType: string
  orderIntakeCustomerReferenceField: boolean
  isConfigurableSlots: boolean
  isAutoConfirmed: boolean
  mutateAsync: (params: {
    orderIntakePayload: SplitLoadOrderIntakePayload
    isConfigurableSlots: boolean
    isAutoConfirmed: boolean
  }) => Promise<unknown>
  setDeliveries: (deliveries: OrderRequest[]) => void
  setPlaceOrderStatus: (status: boolean) => void
  setOrderSummaryDataArray: (data: OrderRequestGroupResponse[]) => void
  setOrderSummary: (data: OrderRequestGroupSuccessResponse['result']) => void
  setShowConfirmationStatusScreen: (show: boolean) => void
  setSplitLoadPayloads: (payloads: SplitLoadOrderIntakePayload[]) => void
  shouldUseV2: boolean
  orgUnitId: string
  country: string
  allocatorInstructions?: string
  siteId: string
  mutateAsyncV2: (params: {orderIntakePayload: V2SplitLoadOrderIntakePayload}) => Promise<unknown>
}

interface MaterialEditorController {
  activeMaterialForm: string | undefined
}

/**
 * Hook that provides the split load submit handler for delivery orders.
 */
export const useSplitLoadSubmit = (params: UseSplitLoadSubmitParams) => {
  const {
    customerId,
    defaultDeliveryBusinessLineCode,
    hasSiteContactNameAndPhone,
    defaultQuantityType,
    orderIntakeCustomerReferenceField,
    isConfigurableSlots,
    isAutoConfirmed,
    mutateAsync,
    setDeliveries,
    setPlaceOrderStatus,
    setOrderSummaryDataArray,
    setOrderSummary,
    setShowConfirmationStatusScreen,
    setSplitLoadPayloads,
    shouldUseV2,
    orgUnitId,
    country,
    allocatorInstructions,
    siteId,
    mutateAsyncV2
  } = params

  const onSplitLoadSubmit =
    (materialEditorController: MaterialEditorController): SubmitHandler<BulkOrderFormValues> =>
    (data) => {
      if (materialEditorController.activeMaterialForm) return

      // Validate required data
      if (!customerId) {
        console.error('Customer ID is required for split load orders')
        return
      }

      // Get selected split load materials array (one per order)
      const selectedSplitLoadMaterials = data.selectedSplitLoadMaterials || []

      // Validate that we have split load materials for each order
      if (selectedSplitLoadMaterials.length !== data.orders.length) {
        console.error('Mismatch between orders and split load materials')
        return
      }

      // Build payloads for each order using buildSplitLoadPayload
      const splitLoadPayloads = data.orders.map((order, index) => {
        const splitLoadMaterialsForOrder = selectedSplitLoadMaterials[index]

        if (!splitLoadMaterialsForOrder) {
          console.error(`No split load materials found for order ${index}`)
          return null
        }

        // Build shared order data from the order and form data
        const sharedOrderData = {
          isSendingConfirmationEmailUnChecked: !data.isConfirmationMailChecked,
          siteNumber: order.payload.siteNumber,
          contact: {
            mainPhone: data.contact.mainPhone || '',
            backupPhone:
              isEmpty(data.contact?.backupPhone) && hasSiteContactNameAndPhone
                ? data.contact?.mainPhone || ''
                : data.contact?.backupPhone || '',
            fullName: data.contact.fullName || '',
            siteContactName:
              isEmpty(data.contact?.siteContactName) && hasSiteContactNameAndPhone
                ? data.contact?.fullName
                : data.contact?.siteContactName,
            carbonCopyEmail: data.contact?.carbonCopyEmail
          },
          customerReference: order.payload.customerReference || DEFAULT_CUSTOMER_REFERENCE,
          shippingType: ShippingType.DELIVER,
          customerId: customerId,
          businessLineCode: defaultDeliveryBusinessLineCode,
          deliveryDate: order.payload.deliveryDate,
          deliveryTime: {
            earliest: order.payload.deliveryTime.earliest,
            latest: order.payload.deliveryTime.latest
          },
          slot: order.payload.slot
            ? {
                loadingStation: order.payload.slot.loadingStation,
                siloNumber: order.payload.slot.siloNumber,
                externalReference: order.payload.slot.externalReference
              }
            : undefined,
          additionalDriverInfo: order.payload.additionalDriverInfo,
          haulerInfo: order.payload.haulerInfo
        }

        // Build the split load payload using the utility function
        const payload = buildSplitLoadPayload({
          splitLoadMaterials: splitLoadMaterialsForOrder,
          sharedOrderData
        })

        return payload
      })

      // Filter out any null payloads and type-narrow to non-null
      const validPayloads = splitLoadPayloads.filter(
        (p): p is SplitLoadOrderIntakePayload => p !== null
      )

      if (validPayloads.length === 0) {
        console.error('No valid split load payloads generated')
        return
      }

      // Set deliveries for state management (convert payloads back to OrderRequest format)
      const updatedDeliveries = data.orders.map((order) => ({
        ...order,
        payload: {
          ...order.payload,
          isSendingConfirmationEmailUnChecked: !data.isConfirmationMailChecked,
          contact: {
            mainPhone: data.contact.mainPhone || '',
            backupPhone:
              isEmpty(data.contact?.backupPhone) && hasSiteContactNameAndPhone
                ? data.contact?.mainPhone || ''
                : data.contact?.backupPhone || '',
            fullName: data.contact.fullName || '',
            siteContactName:
              isEmpty(data.contact?.siteContactName) && hasSiteContactNameAndPhone
                ? data.contact?.fullName
                : data.contact?.siteContactName,
            carbonCopyEmail: data.contact?.carbonCopyEmail
          }
        }
      }))

      setDeliveries(updatedDeliveries as OrderRequest[])
      setPlaceOrderStatus(true)
      setSplitLoadPayloads(validPayloads)

      const finalMutateAsync = (payload: SplitLoadOrderIntakePayload) => {
        if (shouldUseV2) {
          const v2Payload = mapSplitLoadPayloadToV2({
            payload,
            orgUnitId,
            country,
            allocatorInstructions,
            siteId
          })

          return mutateAsyncV2({orderIntakePayload: v2Payload})
        } else {
          return mutateAsync({
            orderIntakePayload: payload,
            isConfigurableSlots,
            isAutoConfirmed
          })
        }
      }

      // Submit all split load orders in parallel
      Promise.all(
        validPayloads.map((payload, index) =>
          finalMutateAsync(payload)
            .then((result) => {
              return {
                index,
                result,
                isSuccess: true,
                delivery: updatedDeliveries[index]
              } as OrderRequestGroupSuccessResponse
            })
            .catch((error) => {
              return {
                index,
                result: error,
                isSuccess: false,
                delivery: updatedDeliveries[index]
              } as OrderRequestGroupErrorResponse
            })
        )
      )
        .then((dataArray) => {
          setOrderSummaryDataArray(dataArray)

          const successfulResults = dataArray.filter(
            (data) => data.isSuccess
          ) as OrderRequestGroupSuccessResponse[]

          successfulResults.forEach((data) => {
            setOrderSummary(data.result)
          })

          setPlaceOrderStatus(false)
          setShowConfirmationStatusScreen(true)
        })
        .catch((error) => {
          const dataArray: OrderRequestGroupResponse[] = updatedDeliveries.map(
            (collection, index) => {
              return {
                index,
                result: error,
                isSuccess: false,
                delivery: collection
              } as OrderRequestGroupErrorResponse
            }
          )

          setOrderSummaryDataArray(dataArray)
          setPlaceOrderStatus(false)
          setShowConfirmationStatusScreen(true)
        })

      // Track analytics for split load submission
      trackEvent('hubOrderIntakeEnd', {
        shippingType: ShippingType.DELIVER,
        isSplitLoad: true,
        numberOfMaterials: selectedSplitLoadMaterials.reduce(
          (total, splitLoad) => total + splitLoad.materials.length,
          0
        ),
        businessLine: defaultDeliveryBusinessLineCode,
        quantityType: defaultQuantityType,
        orderVolume: selectedSplitLoadMaterials.reduce(
          (result, splitLoad) => {
            splitLoad.materials.forEach((material) => {
              result[material.materialNumber] =
                (result[material.materialNumber] || 0) + material.capacity.quantity
            })
            return result
          },
          {} as Record<string, number>
        ),
        numberOfOrderRequests: validPayloads.length,
        purchaseOrderNumberVisible: orderIntakeCustomerReferenceField,
        type: LoadType.Split,
        materials: selectedSplitLoadMaterials.flatMap((splitLoad) =>
          splitLoad.materials.map((m) => String(m.materialDescription ?? m.materialNumber))
        )
      })
    }

  return {onSplitLoadSubmit}
}
